import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '../../../core/data/routes';
import { useAuthStore } from '../store/authStore';
import { Validator, formatGhanaCard } from '../../../core/utils/validator';
import {
	Field,
	LoadingWidget,
	AppBar,
	FadeInAnimation,
	LogoSvg,
} from '../../../core/components/ui';
import { RegisterModel } from '../../../core/types';
import toast from 'react-hot-toast';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { isValidPhoneNumber } from 'react-phone-number-input';

interface FormState {
	firstName: string;
	lastName: string;
	email: string;
	phone: string;
	postalAddress: string;
	nationalId: string;
	tin: string;
	country: string;
}

interface Errors {
	firstName?: string | null;
	lastName?: string | null;
	email?: string | null;
	phone?: string | null;
	postalAddress?: string | null;
	nationalId?: string | null;
}

const pageStyle: React.CSSProperties = {
	minHeight: '100%',
	alignItems: 'center',
	justifyContent: 'center',
	display: 'flex',
	flexDirection: 'column',
	padding: '24px 20px 0',
};

const scrollAreaStyle: React.CSSProperties = {
	flex: 1,
	overflowY: 'auto',
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'center',
	padding: '24px 20px 0',
};

const innerStyle: React.CSSProperties = {
	width: '100%',
	maxWidth: 'var(--form-max-width)',
	paddingBottom: 24,
};

const subtitleStyle: React.CSSProperties = {
	fontSize: 14,
	color: 'var(--color-primary)',
	marginBottom: 24,
	lineHeight: 1.5,
};

const formColsStyle: React.CSSProperties = {
	display: 'flex',
	flexDirection: 'column',
	gap: 24,
	borderRadius: 28,
	padding: '32px 24px 40px',
};

const rowStyle: React.CSSProperties = {
	display: 'grid',
	gridTemplateColumns: '1fr 1fr',
	gap: 12,
};

const footerStyle: React.CSSProperties = {
	position: 'sticky',
	bottom: 0,
	background: 'var(--color-primary-dark)',
	borderTop: '1px solid rgba(255,255,255,0.1)',
	padding: '14px 20px',
	textAlign: 'center',
};

const footerTextStyle: React.CSSProperties = {
	fontSize: 12,
	color: 'rgba(255,255,255,0.6)',
	lineHeight: 1.7,
	margin: 0,
};

const linkStyle: React.CSSProperties = {
	color: 'var(--color-secondary)',
	fontWeight: 600,
};

const RegisterPage: React.FC = () => {
	const navigate = useNavigate();
	const authStore = useAuthStore();
	const [loading, setLoading] = useState(false);

	const [form, setForm] = useState<FormState>({
		firstName: '',
		lastName: '',
		email: '',
		phone: '',
		postalAddress: '',
		nationalId: '',
		tin: '',
		country: 'Ghana',
	});

	const [errors, setErrors] = useState<Errors>({});

	const update = (field: keyof FormState, value: string) => {
		setForm((f) => ({ ...f, [field]: value }));
		setErrors((e) => ({ ...e, [field]: null }));
	};

	const validate = (): boolean => {
		const e: Errors = {
			firstName: Validator.firstname(form.firstName),
			lastName: Validator.surname(form.lastName),
			email: Validator.email(form.email),
			phone: !form.phone
				? 'Phone number is required.'
				: !isValidPhoneNumber(form.phone)
					? 'Enter a valid phone number.'
					: null,
			nationalId: Validator.id(form.nationalId),
			postalAddress:
				form.postalAddress.trim() === ''
					? 'Please enter your postal address.'
					: null,
		};
		setErrors(e);
		return !Object.values(e).some(Boolean);
	};

	const handleRegister = async () => {
		if (!validate()) return;
		setLoading(true);
		try {
			const payload: RegisterModel = {
				firstName: form.firstName.trim(),
				lastName: form.lastName.trim(),
				email: form.email.trim(),
				primaryPhoneNo: form.phone.trim(),
				postalAddress: form.postalAddress.trim(),
				nationalId: form.nationalId.trim(),
				tin: form.tin.trim(),
				country: form.country,
			};
			const result = await authStore.register(payload);
			if (result?.message && !result?.id) {
				toast.error(result.message);
				setLoading(false);
				return;
			}
			await authStore.sendOtp(form.phone);
			navigate(ROUTES.OTP, {
				state: { phoneNumber: form.phone, user: result, isLogin: false },
			});
		} catch (err) {
			toast.error('Registration failed. Please try again.');
		} finally {
			setLoading(false);
		}
	};

	return (
		<div style={pageStyle}>
			{loading && <LoadingWidget />}
			{/* <AppBar title="Create Account" onBack={() => navigate(ROUTES.LOGIN)} /> */}
			<div style={scrollAreaStyle}>
				<FadeInAnimation delay={0}>
					<LogoSvg
						size={100}
						style={{
							filter:
								'brightness(0) saturate(100%) invert(20%) sepia(43%) saturate(600%) hue-rotate(152deg) brightness(95%)',
						}}
					/>{' '}
				</FadeInAnimation>
				<div style={innerStyle}>
					<p style={subtitleStyle}>
						Please provide accurate details. This information will be used to
						verify your identity.
					</p>

					<div style={formColsStyle}>
						{/* Row 1: First Name + Last Name */}
						<div style={rowStyle}>
							<FadeInAnimation delay={0}>
								<Field
									label='First Name'
									error={errors.firstName}>
									<input
										className={`input${errors.firstName ? ' error' : ''}`}
										placeholder='First name'
										value={form.firstName}
										onChange={(e) => update('firstName', e.target.value)}
									/>
								</Field>
							</FadeInAnimation>
							<FadeInAnimation delay={0.1}>
								<Field
									label='Last Name'
									error={errors.lastName}>
									<input
										className={`input${errors.lastName ? ' error' : ''}`}
										placeholder='Last name'
										value={form.lastName}
										onChange={(e) => update('lastName', e.target.value)}
									/>
								</Field>
							</FadeInAnimation>
						</div>

						{/* Row 2: Email + Phone */}
						<div style={rowStyle}>
							<FadeInAnimation delay={0.2}>
								<Field
									label='Email Address'
									error={errors.email}>
									<input
										className={`input${errors.email ? ' error' : ''}`}
										type='email'
										placeholder='example@email.com'
										value={form.email}
										onChange={(e) => update('email', e.target.value)}
										autoComplete='email'
									/>
								</Field>
							</FadeInAnimation>
							<FadeInAnimation delay={0.3}>
								<Field
									label='Phone Number'
									error={errors.phone}>
									<PhoneInput
										international
										defaultCountry='GH'
										value={form.phone}
										onChange={(value) => update('phone', value || '')}
									/>
								</Field>
							</FadeInAnimation>
						</div>

						{/* Row 3: National ID + TIN */}
						<div style={rowStyle}>
							<FadeInAnimation delay={0.4}>
								<Field
									label='National ID'
									error={errors.nationalId}>
									<input
										className={`input${errors.nationalId ? ' error' : ''}`}
										placeholder='GHA-XXXXXXXXX-X'
										value={form.nationalId}
										onChange={(e) =>
											update('nationalId', formatGhanaCard(e.target.value))
										}
										maxLength={16}
									/>
								</Field>
							</FadeInAnimation>
							<FadeInAnimation delay={0.5}>
								<Field label='TIN (Optional)'>
									<input
										className='input'
										placeholder='Tax Identification Number'
										value={form.tin}
										onChange={(e) => update('tin', e.target.value)}
									/>
								</Field>
							</FadeInAnimation>
						</div>

						{/* Row 4: Postal Address + Country */}
						<div style={rowStyle}>
							<FadeInAnimation delay={0.6}>
								<Field
									label='Postal Address'
									error={errors.postalAddress}>
									<input
										className={`input${errors.postalAddress ? ' error' : ''}`}
										placeholder='Your postal address'
										value={form.postalAddress}
										onChange={(e) => update('postalAddress', e.target.value)}
									/>
								</Field>
							</FadeInAnimation>
							<FadeInAnimation delay={0.7}>
								<Field label='Country'>
									<select
										className='input select'
										value={form.country}
										onChange={(e) => update('country', e.target.value)}>
										<option value='Ghana'>Ghana</option>
										<option value='Zambia'>Zambia</option>
										<option value='Nigeria'>Nigeria</option>
										<option value='Kenya'>Kenya</option>
										<option value='Other'>Other</option>
									</select>
								</Field>
							</FadeInAnimation>
						</div>

						{/* CTA */}
						<FadeInAnimation delay={0.8}>
							<div style={{ paddingTop: 8 }}>
								<button
									className='btn-gradient'
									onClick={handleRegister}
									disabled={loading}>
									Create Account
								</button>
							</div>
						</FadeInAnimation>
						<FadeInAnimation delay={0.8}>
							<div
								style={{
									textAlign: 'center',
									fontSize: 14,
									color: 'var(--color-on-surface-variant)',
								}}>
								Already have an account?{' '}
								<button
									type='button'
									onClick={() => navigate(ROUTES.LOGIN)}
									style={{
										background: 'none',
										color: 'var(--color-primary)',
										fontWeight: 700,
										fontSize: 14,
										padding: 0,
									}}>
									Login
								</button>
							</div>
						</FadeInAnimation>
					</div>
				</div>
			</div>

			{/* Sticky footer */}

			<FadeInAnimation
				delay={0.9}
				style={{ marginTop: 'auto', width: '100%' }}>
				<footer
					style={{
						padding: '20px 24px max(24px, env(safe-area-inset-bottom))',
						textAlign: 'center',
						fontSize: 12,
						color: 'var(--color-primary)',
						lineHeight: 1.55,
					}}>
					By continuing, you agree to our{' '}
					<a
						href='https://therappconnect.com/terms'
						target='_blank'
						rel='noreferrer'
						style={{ color: 'var(--color-splash-gold)', fontWeight: 600 }}>
						Terms &amp; Conditions
					</a>
				</footer>
			</FadeInAnimation>
		</div>
	);
};

export default RegisterPage;
